/** SUNTRONIX ONE — FINANCE VAULT. Private financial document workspace.
 *  Server-side enforced access: corporate (config roles) + personal (owner emails). */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Vault, Plus, UploadCloud, ShieldAlert } from "lucide-react";
import {
  Overview, DocExplorer, BankSection, PacksSection, MissingSection,
  HistorySection, ConfigSection,
} from "./FinanceVaultSections";
import { UploadDialog, BulkUploadDialog } from "./FinanceVaultDialogs";
import { SensitiveWatermark } from "@/components/app/SensitiveWatermark";

export const TAX_TYPES = ["INCOME_TAX_RETURN", "ITR_ACKNOWLEDGEMENT",
  "INCOME_TAX_COMPUTATION", "AIS", "TIS", "FORM_26AS", "TAX_AUDIT_DOCUMENT",
  "CAPITAL_GAINS_STATEMENT", "OTHER_TAX_DOCUMENT"];
export const LOAN_TYPES = ["LOAN_STATEMENT", "OD_CC_STATEMENT"];

export default function FinanceVault() {
  const [access, setAccess] = useState(null);
  const [denied, setDenied] = useState(false);
  const [fy, setFy] = useState("");
  const [section, setSection] = useState("OVERVIEW");
  const [upload, setUpload] = useState(null);   // preset object or null
  const [bulk, setBulk] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    api.get("/finance-vault/access")
      .then(({ data }) => {
        if (!data.corporate && !data.personal) { setDenied(true); return; }
        setAccess(data); setFy((prev) => prev || data.fy_now);
      })
      .catch(() => setDenied(true));
  }, []);

  if (denied) return (
    <div className="max-w-md mx-auto mt-20 text-center space-y-3" data-testid="fv-denied">
      <ShieldAlert className="h-10 w-10 mx-auto text-slate-300" />
      <div className="text-sm text-slate-500">Finance Vault access is restricted.</div>
    </div>);
  if (!access) return null;

  const sections = [
    "OVERVIEW",
    ...(access.corporate ? ["SUNTRONIX CORPORATE"] : []),
    ...(access.personal ? ["PERSONAL"] : []),
    "ITR / INCOME TAX", "BALANCE SHEETS", "PROFIT & LOSS", "BANK STATEMENTS",
    "CA / AUDITOR", "LOANS / BANKING", "DOCUMENT PACKS", "MISSING DOCUMENTS",
    "HISTORY",
    ...(access.config_admin ? ["CONFIGURATION"] : []),
  ];
  const common = { access, fy, refreshKey, bump,
    openUpload: (preset) => setUpload(preset || {}) };
  const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "");

  return (
    <div className="max-w-[1500px] mx-auto space-y-4 relative" data-testid="finance-vault-page">
      <SensitiveWatermark area="FINANCE_VAULT" />
      <header className="flex flex-wrap items-center gap-3 bg-slate-900 text-white rounded-xl px-4 py-3">
        <div className="h-10 w-10 rounded-lg bg-amber-400/90 text-slate-900 grid place-items-center">
          <Vault className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="text-lg font-heading font-semibold tracking-wide">FINANCE VAULT</div>
          <div className="text-[10.5px] text-slate-300">
            Private financial documents · {access.corporate ? "Corporate" : ""}
            {access.corporate && access.personal ? " + " : ""}
            {access.personal ? "Personal" : ""} · audited access
          </div>
        </div>
        <select value={fy} onChange={(e) => setFy(e.target.value)} data-testid="fv-fy-selector"
            className="text-[12px] bg-slate-800 border border-slate-700 rounded-md px-2.5 py-1.5">
          {access.fy_options.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <button onClick={() => setBulk(true)} data-testid="fv-bulk-upload"
            className="text-[11px] px-2.5 py-1.5 rounded-md border border-slate-600 inline-flex items-center gap-1">
          <UploadCloud className="h-3 w-3" /> Bulk upload</button>
        <button onClick={() => setUpload({ financial_year: fy })} data-testid="fv-add-document"
            className="text-[11px] px-2.5 py-1.5 rounded-md bg-amber-400 text-slate-900 font-semibold inline-flex items-center gap-1">
          <Plus className="h-3 w-3" /> Add Financial Document</button>
      </header>

      <div className="flex gap-4 items-start">
        <nav className="w-48 shrink-0 space-y-0.5" data-testid="fv-section-nav">
          {sections.map((s) => (
            <button key={s} onClick={() => setSection(s)}
                data-testid={`fv-nav-${slug(s)}`}
                className={"w-full text-left text-[11px] px-2.5 py-2 rounded-md " +
                  (section === s ? "bg-slate-900 text-white font-semibold"
                                 : "text-slate-600 hover:bg-slate-100")}>
              {s}</button>))}
        </nav>
        <div className="flex-1 min-w-0">
          {section === "OVERVIEW" && <Overview {...common} goto={setSection} />}
          {section === "SUNTRONIX CORPORATE" &&
            <DocExplorer {...common} entity="SUNTRONIX_CORPORATE" title="Suntronix Corporate documents" />}
          {section === "PERSONAL" &&
            <DocExplorer {...common} entity="PERSONAL" title="Personal documents (owner only)" />}
          {section === "ITR / INCOME TAX" &&
            <DocExplorer {...common} types={TAX_TYPES} title="ITR / Income Tax documents" />}
          {section === "BALANCE SHEETS" &&
            <DocExplorer {...common} types={["BALANCE_SHEET"]} title="Balance Sheets" />}
          {section === "PROFIT & LOSS" &&
            <DocExplorer {...common} types={["PROFIT_AND_LOSS"]} title="Profit & Loss" />}
          {section === "BANK STATEMENTS" && <BankSection {...common} />}
          {section === "CA / AUDITOR" &&
            <DocExplorer {...common} caTagged title="CA / Auditor documents"
                types={["CA_CERTIFICATE", "AUDIT_REPORT"]} caMode />}
          {section === "LOANS / BANKING" &&
            <DocExplorer {...common} types={LOAN_TYPES} title="Loans / Banking documents" />}
          {section === "DOCUMENT PACKS" && <PacksSection {...common} />}
          {section === "MISSING DOCUMENTS" && <MissingSection {...common} />}
          {section === "HISTORY" && <HistorySection {...common} />}
          {section === "CONFIGURATION" && <ConfigSection {...common} />}
        </div>
      </div>

      {upload && <UploadDialog access={access} preset={upload}
          onClose={() => setUpload(null)}
          onDone={() => { setUpload(null); bump(); }} />}
      {bulk && <BulkUploadDialog access={access} fy={fy}
          onClose={() => setBulk(false)}
          onDone={() => { setBulk(false); bump(); }} />}
    </div>
  );
}
